/**
 * The "cofactor.md" canvas view: renders the current flow as its `.cofactor.md`
 * spec, beautifully and read-only.
 *
 * Presentational — it just renders a block model (see {@link useFlowSpec}).
 * Copy/Download live in the canvas chrome ({@link module:FlowDesigner/spec/SpecActions}),
 * and the flow name/tags are already shown in the designer header, so neither is
 * repeated here.
 *
 * @module FlowDesigner/spec/FlowSpecView
 */

import React from 'react';
import { Markdown } from '@chatsdet/ui';
import type { SpecDocBlock } from '@chatsdet/canary-spec/browser';
import { SpecStepBlock, type StepStatus } from './SpecStepBlock';

export interface FlowSpecViewProps {
  blocks: SpecDocBlock[];
  /** Live seams (unused today — passing nothing keeps the view read-only). */
  currentStepId?: string | null;
  stepStatusById?: Record<string, StepStatus>;
}

export const FlowSpecView: React.FC<FlowSpecViewProps> = ({
  blocks,
  currentStepId,
  stepStatusById,
}) => {
  let stepIndex = 0;
  return (
    <div className="h-full overflow-auto bg-muted/20">
      <div className="mx-auto flex max-w-3xl flex-col gap-3 px-6 py-8">
        {blocks.map((block, i) => {
          // The flow name + tags live in the designer header — don't repeat them.
          if (block.kind === 'frontmatter') return null;
          if (block.kind === 'section') {
            return (
              <Markdown key={`section-${i}`} className="text-sm">
                {block.markdown}
              </Markdown>
            );
          }
          stepIndex += 1;
          return (
            <SpecStepBlock
              key={block.nodeId}
              index={stepIndex}
              nodeId={block.nodeId}
              nodeType={block.nodeType}
              title={block.title}
              description={block.description}
              assertions={block.assertions}
              details={block.details}
              status={stepStatusById?.[block.nodeId]}
              isCurrent={currentStepId === block.nodeId}
            />
          );
        })}
      </div>
    </div>
  );
};
